import _ from 'lodash';

import createEnum from 'scripts/lib/create_enum';
import Err from 'models/err';
import ErrorReporter from 'scripts/infrastructure/error_reporter';
import PhoneGatewayBase from './phone_gateway_base';
import qconsole from 'scripts/lib/qconsole';
import { TwilioDeviceStatus } from 'models/connection_states';

export const TwilioWarnings = createEnum('low-mos', 'high-rtt', 'high-jitter', 'high-packet-loss');

// Maximum number of times the Twilio client device should try to setup
const MAX_RETRY_COUNT = 10;

// Maximum duration the Twilio connection should be in the 'connecting' status
const MAX_CONNECTING_DURATION = 20000;

const TOKEN_FETCH_INTERVAL = 10000;

export default class TwilioPhoneGateway extends PhoneGatewayBase {
  constructor(backend, requestorId, twilio) {
    super(backend, requestorId);
    this.twilio = twilio;
    this.setupAttempt = 0;
    this.allowIncomingWhileBusy = false;
    this.registerAgent = _.throttle(this._registerAgent, TOKEN_FETCH_INTERVAL);
  }

  get version() {
    return '1';
  }

  get topicPattern() {
    return 'configuration/twilio-token/+';
  }

  get registeredAgentId() {
    if (this._registeredAgentId) {
      return this._registeredAgentId;
    }
    qconsole.log('No registeredAgentId for the twilio phone gateway. Ensure you called registerAgent first.');
    return null;
  }

  setAllowIncomingCallsWhileBusy(isEnabled) {
    this.allowIncomingWhileBusy = isEnabled;
  }

  _registerAgent(agentId) {
    if (!agentId) {
      ErrorReporter.reportError(new Error('TwilioPhoneGateway error'), {
        message: 'missing agentId - phone gateway will not start up',
      });
      return;
    }
    this._registeredAgentId = agentId;
    super._fetch([agentId]);
    this.notifyObservers('handleTwilioEnabled', null);
    let twilioConnectingStatus = this._callbacksRegistered
      ? TwilioDeviceStatus.RECONNECTING
      : TwilioDeviceStatus.CONNECTING;
    this.notifyObservers('handlePhoneStatus', twilioConnectingStatus);
  }

  deregisterAgent(agentId) {
    if (this.twilio) {
      this._deviceDestroyed = true;
      this.twilio.Device.destroy();
    }

    this._callbacksRegistered = false;
    this._registeredAgentId = null;
    this.notifyObservers('handleTwilioDisabled', null);
  }

  onFetchResponse(fetchResultDto, topic, parsedTopicParams) {
    this.configureTwilio(fetchResultDto.capabilityToken);
  }

  onFetchError(errorDto, topic, parsedTopicParams) {
    this.notifyObservers('handleRequestError', errorDto);
    // Do not retry if the org does not have a configured telephony gateway
    let unconfiguredGatewayError = _.find(errorDto.errors, { code: Err.Code.INVALID_STATE });
    if (!unconfiguredGatewayError) {
      ErrorReporter.reportError(new Error('twilio token error'), {
        message: 'failed to fetch twilio auth token',
        tags: { errors: JSON.stringify(errorDto) },
      });
      this.registerAgent(this.registeredAgentId);
    }
  }

  configureTwilio(capabilityToken) {
    let codecPreferences = ['opus', 'pcmu'];
    let isTwilioSetupSuccessfully = this.twilio.Device.setup(capabilityToken, {
      allowIncomingWhileBusy: this.allowIncomingWhileBusy,
      debug: true,
      closeProtection: true,
      codecPreferences,
    });
    if (isTwilioSetupSuccessfully) {
      this.setupAttempt = 0;
      this._registerCallbacks();
      this.notifyObservers('handleConnect', null);
    } else {
      ErrorReporter.reportError(new Error('twilio setup error'), {
        message: 'failed to setup twilio device',
        extra: {
          setupAttempt: this.setupAttempt,
        },
      });
      if (this.setupAttempt < MAX_RETRY_COUNT) {
        this.setupAttempt += 1;
        setTimeout(this.registerAgent(this.registeredAgentId), 3000);
      }
      this.notifyObservers('handleDisconnect', null);
    }
  }

  _registerCallbacks() {
    // Unable to register callbacks in constructor because Twilio breaks capybara-webkit
    if (this._callbacksRegistered) {
      return;
    }
    this.twilio.Device.on('ready', device => {
      this.notifyObservers('handleRegisteredAgent', {});
      let deviceStatus = device.status();
      this.notifyObservers('handlePhoneStatus', deviceStatus.toUpperCase());
    });

    this.twilio.Device.on('error', error => {
      // ref: https://www.twilio.com/docs/api/client/errors
      if (error.code === 31205 && error.message === 'JWT Token Expired') {
        // expected error from Twilio every hour
        return;
      }

      let callSid = _.get(error, 'connection.parameters.CallSid');
      if (!callSid) {
        if (error.code === 31005) {
          // WebSocket connection error. Only report a sentry if there was an active call during the connection error
          return;
        }
        callSid = 'no connection when the error occurred';
      }

      ErrorReporter.reportError(new Error('twilio device error'), {
        message: error.message,
        tags: { message: error.message, code: error.code, callSid },
      });

      this.notifyObservers('handleDeviceError', {});
    });

    this.twilio.Device.on('connect', function(connection) {
      qconsole.log('Twilio established a connection');
    });

    this.twilio.Device.on('disconnect', connection => {
      this.connection = null;
      this.notifyObservers('handleHangup', {});
    });

    // Receive Cancel event in one of 4 scenarios
    // 1) Declining call
    // 2) Timing out: Agent did not accept or decline the call before Twilio views call as `no_answer`
    // 2) Answering call in another tab
    // 3) Customer hanging up or Transferring agent canceling before agent has answered
    this.twilio.Device.on('cancel', connection => {
      qconsole.log('Twilio cancel called');
      if (this.connection) {
        this.connection = null;
      } else {
        this.notifyObservers('handleCancelError', 'canceling call without an active connection');
      }
      this.notifyObservers('handleHangup', {});
    });

    this.twilio.Device.on('incoming', connection => {
      const status = this.twilio.Device.status();
      if (status.toUpperCase() !== TwilioDeviceStatus.READY && status.toUpperCase() !== TwilioDeviceStatus.BUSY) {
        ErrorReporter.reportMessage(`unexpected twilio device status on incoming event`, {
          tags: { status },
          extra: {
            allowIncomingWhileBusy: this.allowIncomingWhileBusy,
            callSid: _.get(connection, 'parameters.CallSid'),
          },
        });
      }
      this.connection = connection;
      this.connection.on('error', err => {
        let callSid = err.connection.parameters.CallSid;
        ErrorReporter.reportError(new Error('twilio connection error'), {
          message: err.message,
          tags: { message: err.message, code: err.code, callSid, deviceStatus: this.getDeviceStatus() },
        });
      });
      this.notifyObservers('handleActiveCall', {});
    });

    this.twilio.Device.on('offline', device => {
      let deviceStatus = device.status();
      this.notifyObservers('handlePhoneStatus', deviceStatus.toUpperCase());
      if (this._deviceDestroyed) {
        this._deviceDestroyed = false;
        return;
      }
      this.registerAgent(this.registeredAgentId);
    });

    this._callbacksRegistered = true;
  }

  accept() {
    // There should always be an active connection when accepting a webRTC call.
    // Direct Dial will not use this accept method as the call is answered via physical phone
    if (this.connection) {
      this.connection.accept();
      this.notifyObservers('handleAccept', {});
      this.connection.on('warning', warningName => {
        if (_.keys(TwilioWarnings).indexOf(warningName) !== -1) {
          this.notifyObservers('handleCallQualityWarning', warningName);
        }
      });
      this.connection.on('warning-cleared', warningName => {
        if (_.keys(TwilioWarnings).indexOf(warningName) !== -1) {
          this.notifyObservers('handleCallQualityWarningCleared', warningName);
        }
      });
      let connection = this.connection;
      this._checkConnectionStatus(connection);

      // Device is busy once it has an active call
      this.notifyObservers('handlePhoneStatus', TwilioDeviceStatus.BUSY);
    } else {
      this.notifyObservers('handleAcceptError', 'could not accept call without an active connection');
    }
  }

  call() {
    this.notifyObservers('handleCall', {});
  }

  decline() {
    if (this.connection) {
      this.connection.reject();
    }
  }

  hangup() {
    if (this.connection) {
      this.connection.disconnect();
    } else {
      this.notifyObservers('handleHangup', {});
    }
  }

  sendDigits(digits) {
    if (this.connection) {
      this.connection.sendDigits(digits);
    }
  }

  disconnectActiveConnection() {
    if (this.connection) {
      // reference: https://www.twilio.com/docs/voice/client/javascript/device#disconnect-all
      this.twilio.Device.disconnectAll();
    }
  }

  getConnectionStatus() {
    if (this.connection) {
      return this.connection.status();
    }
    return null;
  }

  getDeviceStatus() {
    let status;
    try {
      // 3 Possible Device statuses: 'ready', 'busy', 'offline'
      status = this.twilio.Device.status().toUpperCase();
    } catch (err) {
      status = TwilioDeviceStatus.OFFLINE;
    }
    return status;
  }

  isDeviceReady() {
    return this.getDeviceStatus() === TwilioDeviceStatus.READY;
  }

  // checks the Twilio connection status after a set amount of time. Twilio connections
  // should either 'closed' or 'open' after the agent has accepted the incoming call.
  // Calls that fail to connect, typically fail within 10s. Report a message to sentry
  // if the connection does not update within the maximum connecting duration
  _checkConnectionStatus(connection) {
    setTimeout(function() {
      let connectionStatus = connection.status();
      if (connectionStatus !== 'connecting') {
        return;
      }
      ErrorReporter.reportMessage('twilio webRTC call failed to connect within 20s', {
        extra: {
          connectionState: this.context.stores.connectionState.get(),
          status: connectionStatus,
          callID: connection.parameters.CallSid,
        },
      });
    }, MAX_CONNECTING_DURATION);
  }
}



// WEBPACK FOOTER //
// ./src/scripts/adapters/gateways/twilio_phone_gateway.js